"use client";

import { Feedback } from "@/components/dashboard/Feedback";
import { useUser } from "@/stores/appStore";
import { useRouter } from "next/navigation";
import { MouseEvent, useCallback, useEffect, useRef, useState } from "react";

const HOME_FEEDBACK_PARAM = "openFeedback";
const HOME_FEEDBACK_SCENE = "home";
const HOME_FEEDBACK_ANCHOR = "site-footer-feedback";

export function SiteFooterFeedbackLink() {
  const router = useRouter();
  const {
    user,
    refreshUserInfo,
  } = useUser();
  const [autoOpenKey, setAutoOpenKey] = useState<number | null>(null);
  const handledAutoOpenRef = useRef(false);

  const getFeedbackReturnUrl = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set(HOME_FEEDBACK_PARAM, HOME_FEEDBACK_SCENE);
    url.hash = HOME_FEEDBACK_ANCHOR;
    return url.toString();
  }, []);

  const removeAutoOpenParam = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete(HOME_FEEDBACK_PARAM);
    url.hash = HOME_FEEDBACK_ANCHOR;
    router.replace(`${url.pathname}${url.search}${url.hash}`, {
      scroll: false,
    });
  }, [router]);

  const ensureLoggedIn = useCallback(async () => {
    if (user?.userId) {
      return true;
    }
    const refreshed = await refreshUserInfo(true);
    if (refreshed?.user?.userId) {
      return true;
    }
    router.push(`/login?callback=${encodeURIComponent(getFeedbackReturnUrl())}`);
    return false;
  }, [getFeedbackReturnUrl, refreshUserInfo, router, user?.userId]);

  const handleClick = useCallback(async (event: MouseEvent<HTMLButtonElement>) => {
    if (user?.userId) {
      return;
    }
    event.preventDefault();
    const loggedIn = await ensureLoggedIn();
    if (loggedIn) {
      setAutoOpenKey(Date.now());
    }
  }, [ensureLoggedIn, user?.userId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (handledAutoOpenRef.current || params.get(HOME_FEEDBACK_PARAM) !== HOME_FEEDBACK_SCENE) {
      return;
    }
    handledAutoOpenRef.current = true;
    document.getElementById(HOME_FEEDBACK_ANCHOR)?.scrollIntoView({
      block: "center",
    });
    void (async () => {
      const loggedIn = await ensureLoggedIn();
      if (loggedIn) {
        setAutoOpenKey(Date.now());
        removeAutoOpenParam();
      }
    })();
  }, [ensureLoggedIn, removeAutoOpenParam]);

  return (
    <Feedback
      scene="home"
      autoOpenKey={autoOpenKey}
      onBeforeOpen={ensureLoggedIn}
    >
      <button
        id={HOME_FEEDBACK_ANCHOR}
        type="button"
        className="w-fit text-left hover:underline"
        onClick={(event) => {
          void handleClick(event);
        }}
      >
        建议反馈
      </button>
    </Feedback>
  );
}
